import { playTurnStart } from "./SoundManager.js";

/**
 * Edge detector for the "it's your turn now" signal. Every game recomputes
 * turn ownership on a different cadence — per engine transition (backgammon),
 * per state mutation (checkers, domino), per authoritative snapshot (mahjong
 * online) — and several of them recompute it many times within one turn. The
 * signal must fire on the *transition* into the local player's turn, exactly
 * once, so the shared piece is this: callers push the current answer as often
 * as they like and only a false → true edge sounds.
 *
 * What counts as "the local player's turn" is the caller's call, and so is
 * whether the mode deserves a signal at all — hot-seat games skip it, since
 * the human who just moved is the same human being told to move.
 */
export interface TurnSignalOptions {
  /** Read fresh on every edge so a Settings toggle takes effect mid-game. */
  readonly isEnabled: () => boolean;
  /** Override the sound (tests, or a game with its own cue). */
  readonly play?: () => void;
}

export class TurnSignal {
  private readonly isEnabled: () => boolean;
  private readonly play: () => void;
  private wasLocalTurn = false;

  constructor(options: TurnSignalOptions) {
    this.isEnabled = options.isEnabled;
    this.play = options.play ?? playTurnStart;
  }

  /**
   * Report whose turn it is now. Sounds only on the entry edge; the edge is
   * tracked whether or not the setting is on, so turning the setting on
   * mid-turn doesn't fire retroactively for a turn already in progress.
   */
  update(isLocalTurn: boolean): void {
    const wasLocalTurn = this.wasLocalTurn;
    this.wasLocalTurn = isLocalTurn;
    if (!isLocalTurn || wasLocalTurn) return;
    if (!this.isEnabled()) return;
    this.play();
  }

  /**
   * Forget the last answer. Call when a game ends, a new one starts, or the
   * scene unmounts — otherwise a fresh game that opens on the local player's
   * turn is swallowed as "already true", or the *previous* game's last turn
   * leaks an edge into the new one.
   */
  reset(): void {
    this.wasLocalTurn = false;
  }
}
